import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Hold every `<sellwild-ad>` insertion until the page has stopped producing
 * them, then release the whole batch in one commit.
 *
 * WHY. The widget starts a Prebid auction from a tag's connectedCallback, and it
 * debounces those by only ~16ms. Each auction ends with
 * `googletag.pubads().refresh(swpbjs.gamSlots)` — the WHOLE array — and
 * `setTargetingForGPTAsync` first CLEARS the existing hb_* keys and writes the
 * current round's. So a second auction seconds later, where SSPs commonly no-bid
 * or bid down, wipes the first round's high-CPM targeting and re-requests with
 * little external demand; GAM fills from remnant. The impression a reader ends
 * up looking at is priced at the LAST round, not the best one.
 *
 * A page load produced three such rounds, and the app guaranteed it:
 *
 *  1. The hydration commit renders every AdFramework as a blank placeholder, and
 *     the passive-effect flush then batches `mounted`, the partner and
 *     `clientSide` into one re-render — the header and sidebar tags insert.
 *  2. That same commit is when `clientSide` first turns true, which is what
 *     unlocks the in-feed ads. Those are brand new components, so they render a
 *     placeholder in that commit and only insert on their own NEXT one.
 *  3. Rows whose listings arrive from fetch mount later and repeat the two-phase
 *     insert.
 *
 * Because a new ad's tag always lands in a later commit than the one that
 * created it, it can never be batched with its siblings — which maximised the
 * number of distinct auctions. Batching here converts three full-page refresh
 * rounds into one.
 *
 * This is a STOPGAP. The real fix is in the widget, whose master already
 * refreshes only slots that have never loaded; the deployed bundle is far behind
 * it. This cannot help scroll-triggered rounds either — the deployed widget
 * still refreshes every slot when a later ad mounts — it only reduces how many
 * of those rounds a page produces.
 */

/**
 * How long to wait for the page to stop creating ads. Reset by each new
 * registration, so a burst settles once rather than per ad.
 *
 * Long enough to span a React commit plus the effect flush that follows it,
 * which is what separates waves 1 and 2. Short enough that an ad above the fold
 * is not visibly late.
 */
export const AD_INSERT_SETTLE_MS = 300;

/**
 * Ceiling on the delay for any one batch, measured from its first registration.
 *
 * Without it a feed that keeps mounting rows every 200ms would reset the settle
 * timer forever and no ad would ever render. The cap trades a possible extra
 * auction for the guarantee that ads appear.
 */
export const AD_INSERT_MAX_WAIT_MS = 1200;

/**
 * Ceiling on how long a hold may suppress a release, measured from when the
 * first hold was taken.
 *
 * A hold is a promise that more ads are coming, and a page that never keeps it
 * — a product whose fetch fails, a skeleton that never resolves — must not cost
 * the ads that are already waiting. After this the hold is ignored and the
 * batch releases on its own timers.
 *
 * Generous on purpose. Exceeding it does not degrade gracefully: it puts the
 * page back into the two-batch, two-auction state this exists to prevent, and
 * the pages slow enough to reach it are exactly the ones where that costs most.
 * A cold ISR product page on staging took 3.8s to swap its skeleton for the
 * body, which a 4s ceiling nearly cut into. Nothing is lost by waiting longer —
 * a page still showing a placeholder after this long has no content to sell
 * against, and the one that never resolves redirects away.
 */
export const AD_INSERT_MAX_HOLD_MS = 8000;

let released = 0;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

/** Ads registered for the batch that has not been released yet. */
let waiting = 0;

/** Outstanding holds — see `acquireAdInsertHold`. */
let holds = 0;
let holdTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Set when the hold ceiling fires. Kept until the last hold is dropped, so an
 * expired hold cannot re-suspend the batch every time another ad joins.
 */
let holdExpired = false;

function holding(): boolean {
  return holds > 0 && !holdExpired;
}

function emit(): void {
  for (const listener of listeners) listener();
}

function clearTimers(): void {
  if (settleTimer) clearTimeout(settleTimer);
  if (maxWaitTimer) clearTimeout(maxWaitTimer);
  settleTimer = null;
  maxWaitTimer = null;
}

function clearHoldTimer(): void {
  if (holdTimer) clearTimeout(holdTimer);
  holdTimer = null;
}

function release(): void {
  clearTimers();
  waiting = 0;
  released += 1;
  // One emit, so every ad waiting on this batch setStates inside a single
  // callback and React commits them together. Releasing them one at a time
  // would recreate the very problem this exists to solve.
  emit();
}

/**
 * (Re)start the settle window, unless a hold is suspending this batch.
 *
 * While held, the timers are cleared rather than left running: a hold means the
 * page has ads it has not rendered yet, and releasing on schedule would put
 * them in a batch of their own.
 */
function armTimers(): void {
  if (holding()) {
    clearTimers();
    return;
  }

  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(release, AD_INSERT_SETTLE_MS);

  // Started once per batch, not reset by later joins — that is what makes it a
  // ceiling rather than a second settle timer.
  if (!maxWaitTimer) {
    maxWaitTimer = setTimeout(release, AD_INSERT_MAX_WAIT_MS);
  }
}

/**
 * Register interest in the next batch and (re)start its settle window. Returns
 * the batch number the caller belongs to.
 */
function joinNextBatch(): number {
  const batch = released + 1;
  waiting += 1;
  armTimers();
  return batch;
}

function expireHold(): void {
  holdTimer = null;
  holdExpired = true;
  if (waiting > 0) armTimers();
}

/**
 * Keep the current batch open until the caller releases it.
 *
 * WHY. The batcher can only group ads that exist at the same time, and a page
 * that renders a skeleton first does not have its ads yet. On a product page
 * the layout's banner mounts during hydration and released ~450ms before the
 * product body replaced the skeleton and brought its own three frames — two
 * batches, so two auctions, and every auction refreshes EVERY slot on the page.
 * Both refreshes went out before any slot had rendered, which is precisely the
 * window ./ad-refresh-guard cannot cover: it drops slots that already hold a
 * creative, and at that point none did. So every ad on the page was requested
 * twice and visibly repainted, the second time with the round's targeting
 * cleared and re-written — the flash, and the worse ad after it.
 *
 * A holder says "there are more ads coming, do not close the batch yet". Browse
 * pages never needed one because their listings are server-rendered, so every
 * ad mounts in the same commit.
 *
 * Returns the release function. Calling it more than once is a no-op, so it is
 * safe to use directly as an effect cleanup.
 */
export function acquireAdInsertHold(): () => void {
  holds += 1;

  if (holds === 1) {
    clearTimers();
    clearHoldTimer();
    holdTimer = setTimeout(expireHold, AD_INSERT_MAX_HOLD_MS);
  }

  let dropped = false;
  return () => {
    if (dropped) return;
    dropped = true;
    holds -= 1;
    if (holds > 0) return;

    clearHoldTimer();
    holdExpired = false;
    // The ads that arrived while held are already waiting; give them the normal
    // settle window so the ones still mounting in this commit join them.
    if (waiting > 0) armTimers();
  };
}

/**
 * Hold the batch open for as long as `active` is true.
 *
 * Call it from a page that renders a placeholder before its real content, with
 * the same condition that chooses the placeholder. See `acquireAdInsertHold`.
 */
export function useAdInsertHold(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    return acquireAdInsertHold();
  }, [active]);
}

/** Outstanding holds — for tests and diagnostics. */
export function adInsertHoldCount(): number {
  return holds;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Bumped by every reset. An ad that joined a batch before the reset belongs to
 * an older generation and must rejoin rather than sit forever on a batch number
 * the new counter will not reach.
 */
let generation = 0;

// One string so both halves of the state arrive together through
// useSyncExternalStore, which needs a single stable snapshot value.
const getReleased = () => `${generation}:${released}`;
// Nothing is ever released during SSR, so the server always renders the
// placeholder — which is what the hydration-safe path did before this existed.
const getServerReleased = () => '0:0';

/**
 * Start a fresh batching state. Called on a page view alongside the slot budget:
 * a client-side navigation is a new page as far as ads are concerned.
 */
export function resetAdInsertBatch(): void {
  clearTimers();
  waiting = 0;
  released = 0;
  generation += 1;
  emit();
}

/** Batches released so far — for tests and diagnostics. */
export function releasedAdBatches(): number {
  return released;
}

/**
 * True once this ad's batch has been released and its tag may be inserted.
 *
 * `active` is the caller's own readiness (mounted, and holding a slot from the
 * page budget). Passing false keeps the ad out of any batch, so an ad that will
 * never render cannot hold up the ones that will.
 */
export function useAdInsertRelease(active: boolean): boolean {
  const snapshot = useSyncExternalStore(subscribe, getReleased, getServerReleased);
  const [snapGeneration, releasedCount] = snapshot.split(':').map(Number);

  // State, not a ref: the batch number is read during render to decide what to
  // return, and a ref read at render time is not safe to observe.
  const [joined, setJoined] = useState<{ generation: number; batch: number } | null>(null);

  /**
   * Once an ad's tag is in the DOM it stays in. Removing a <sellwild-ad> does
   * not remove its GAM slot, so letting `released` fall back below this ad's
   * batch — which is exactly what a reset did to an already-inserted ad — swaps
   * the live tag for a placeholder and then re-inserts it when some later ad
   * releases the same batch number, defining a SECOND slot for the same space
   * and triggering a full-page refresh.
   */
  const [latched, setLatched] = useState(false);

  useEffect(() => {
    if (!active) return;
    // Rejoin after a reset: an ad still waiting when the batcher restarted is
    // holding a batch number the new counter will never reach, and its guard
    // used to be `batch !== null`, so it waited forever.
    if (joined && joined.generation === snapGeneration) return;
    // Registration with an external scheduler — the whole point of the effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setJoined({ generation: snapGeneration, batch: joinNextBatch() });
  }, [active, joined, snapGeneration]);

  const releasedNow =
    joined !== null && joined.generation === snapGeneration && releasedCount >= joined.batch;

  useEffect(() => {
    if (releasedNow && !latched) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLatched(true);
    }
  }, [releasedNow, latched]);

  return latched || releasedNow;
}
